import { AlertTriangleIcon } from 'lucide-react'
import { AlertDialog } from 'radix-ui'
import { useState } from 'react'
import { create } from 'zustand'
import { cn } from '@/lib/utils'
import { Button } from './button'

interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  /** Si devuelve una promesa, el diálogo muestra carga hasta que termine. */
  onConfirm: () => unknown
}

interface ConfirmState {
  options: ConfirmOptions | null
  open: (options: ConfirmOptions) => void
  close: () => void
}

const useConfirmStore = create<ConfirmState>((set) => ({
  options: null,
  open: (options) => set({ options }),
  close: () => set({ options: null }),
}))

/** Abre un diálogo de confirmación desde cualquier lugar: `confirm({ title, onConfirm })`. */
export const confirm = (options: ConfirmOptions) => useConfirmStore.getState().open(options)

export function ConfirmHost() {
  const { options, close } = useConfirmStore()
  const [loading, setLoading] = useState(false)

  async function handleConfirm() {
    if (!options) return
    setLoading(true)
    try {
      await options.onConfirm()
      close()
    } finally {
      setLoading(false)
    }
  }

  return (
    <AlertDialog.Root open={!!options} onOpenChange={(open) => !open && !loading && close()}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="duration-200 ease-smooth data-[state=closed]:duration-150 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=open]:animate-in" />
        <AlertDialog.Content className="duration-200 ease-smooth data-[state=closed]:duration-150 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.97] data-[state=open]:zoom-in-[0.97] data-[state=open]:slide-in-from-bottom-1 fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-popover p-6 shadow-elevation-3 data-[state=closed]:animate-out data-[state=open]:animate-in">
          {options && (
            <>
              <div className="flex gap-4">
                <div
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-full',
                    options.destructive
                      ? 'bg-destructive-soft text-destructive'
                      : 'bg-primary-soft text-primary',
                  )}
                >
                  <AlertTriangleIcon className="size-5" />
                </div>
                <div className="pt-0.5">
                  <AlertDialog.Title className="font-semibold text-base">{options.title}</AlertDialog.Title>
                  {options.description && (
                    <AlertDialog.Description className="mt-1.5 text-muted-foreground text-sm leading-relaxed">
                      {options.description}
                    </AlertDialog.Description>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <AlertDialog.Cancel asChild>
                  <Button variant="outline" disabled={loading}>
                    {options.cancelLabel ?? 'Cancelar'}
                  </Button>
                </AlertDialog.Cancel>
                <Button
                  variant={options.destructive ? 'destructive' : 'default'}
                  loading={loading}
                  onClick={handleConfirm}
                >
                  {options.confirmLabel ?? 'Confirmar'}
                </Button>
              </div>
            </>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
